import {
  Directive,
  Input,
  OnInit,
  OnDestroy,
  HostBinding,
  inject,
  Host,
  Optional,
  ElementRef,
  Renderer2,
  OnChanges,
  ChangeDetectorRef,
  Output,
  HostListener,
  EventEmitter,
  AfterContentInit,
} from '@angular/core';
import { UserRoleService } from '../../@shared/services/user.role.service';
import { Observable, Subject, takeUntil } from 'rxjs';
import { Dropdown } from '@gridscale/primeng17/dropdown';
import { ButtonComponent } from '@gridscale/ingrid/elements/button';

@Directive({
  selector: '[gsUserRole]',
  standalone: true,
})
export class UserRoleDirective implements OnInit, OnChanges, OnDestroy, AfterContentInit {
  readonly #el = inject(ElementRef);


  @Input() gsUserRole = 'write';
  @Input() disableOnly = false;
  @Input() disabled?: boolean;

  @HostBinding('attr.disabled') get isDisabled() {
    return this.#disabled ? true : undefined;
  }

  @Output() onClickWithRights = new EventEmitter<Event & {
    target: EventTarget | null,
    clientX: number,
    clientY: number,
  }>();
  @HostListener('click', ['$event']) onClick($event: MouseEvent) {
    if (!this.isDisabled) {
      this.onClickWithRights.emit($event);
    }
  }
  @HostListener('keydown', ['$event']) onEnter(e: KeyboardEvent) {
    if (e.code === 'Enter') {
      e.preventDefault();
      this.onClickWithRights.emit({
        ...e,
        currentTarget: this.#el.nativeElement,
        target: this.#el.nativeElement,
        clientY: this.#el.nativeElement.getBoundingClientRect()?.top,
        clientX: this.#el.nativeElement.getBoundingClientRect()?.left,
      });
    }
  }

  readonly #destroyed = new Subject<void>();
  readonly #userRoleService = inject(UserRoleService);
  readonly #elem = inject(ElementRef);
  readonly #renderer = inject(Renderer2);
  readonly #cdRef = inject(ChangeDetectorRef);

  #disabled = true;
  #wasDisabled = false;

  constructor(
    @Host() @Optional() private pDropdown: Dropdown,
    @Host() @Optional() private igButton: ButtonComponent
  ) {}

  ngOnInit(): void {
    if (this.disabled) {
      this.#wasDisabled = true;
    }

    this.#setCustomComponentsDisabledStated();
    this.#disableComponent();

    let obs$: Observable<boolean>;

    switch (this.gsUserRole) {
      case 'read':
        obs$ = this.#userRoleService.isRead$();
        break;

      default:
      case 'write':
        obs$ = this.#userRoleService.isWrite$();
        break;

      case 'admin':
        obs$ = this.#userRoleService.isAdmin$();
        break;
    }

    obs$.pipe(takeUntil(this.#destroyed)).subscribe((isRole: boolean) => {
      if (isRole) {
        this.#enableComponent();
      } else {
        this.#disableComponent();
      }
    });
  }


  ngAfterContentInit() {
    // always add a tabindex so link is focusable
    if ((this.#el.nativeElement as HTMLElement).tagName === 'A' && !(this.#el.nativeElement as HTMLElement).hasAttribute('tabindex')) {
      (this.#el.nativeElement as HTMLElement).setAttribute('tabindex', '0');
    }

  }

  ngOnChanges(): void {
    this.#wasDisabled = !!this.disabled;
  }

  ngOnDestroy(): void {
    this.#destroyed.next();
    this.#destroyed.complete();
  }

  #enableComponent() {
    this.#disabled = false || !!this.#wasDisabled;

    if (this.#checkForCustomElements()) {
      this.#setCustomComponentsDisabledStated();
    } else {
      if (this.disableOnly) {
        this.#renderer.setStyle(this.#elem.nativeElement, 'opacity', '1');
        this.#renderer.removeStyle(this.#elem.nativeElement, 'cursor');
      } else {
        this.#renderer.removeStyle(this.#elem.nativeElement, 'display');
      }
    }

    setTimeout(() => this.#cdRef.detectChanges());
  }

  #disableComponent() {
    this.#disabled = true;

    if (this.#checkForCustomElements()) {
      this.#setCustomComponentsDisabledStated();
    } else {
      if (this.disableOnly) {
        this.#renderer.setStyle(this.#elem.nativeElement, 'opacity', '0.75');
        this.#renderer.setStyle(
          this.#elem.nativeElement,
          'cursor',
          'not-allowed'
        );
      } else {
        this.#renderer.setStyle(this.#elem.nativeElement, 'display', 'none');
      }
    }

    setTimeout(() => this.#cdRef.detectChanges());
  }

  #setCustomComponentsDisabledStated() {
    if (this.pDropdown) {
      this.pDropdown.disabled = this.#disabled;

      if (!this.disableOnly) {
        if (this.#disabled) {
          this.#renderer.setStyle(this.#elem.nativeElement, 'display', 'none');
        } else {
          this.#renderer.removeStyle(this.#elem.nativeElement, 'display');
        }
      }
    }

    if (this.igButton) {
      this.igButton.disabled = this.#disabled;

      if (!this.disableOnly) {
        if (this.#disabled) {
          this.#renderer.setStyle(this.#elem.nativeElement, 'display', 'none');
        } else {
          this.#renderer.removeStyle(this.#elem.nativeElement, 'display');
        }
      }
    }
  }

  #checkForCustomElements() {
    return this.pDropdown || this.igButton;
  }
}
